import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { MasterModalProps, MasteringPresets, navButtons } from "./types";
import {
  BottomButtonsContainer,
  DetailsContainer,
  GoBackButtonContainer,
  ModalContainer,
  NavButtons,
  styles,
} from "./styles";
import { Details } from "./Details";
import { ChoosePreset } from "./ChoosePreset";
import { Preview } from "./Preview";
import { CreateVersion } from "./CreateVersion";
import Mixpanel from "../../utils/mixpanel";
import { useGetCustomerPortalLink } from "../../hooks/useGetCustomerPortalLink";
import { APP_COLOR, TEXT_COLOR } from "../../theme";

export const MasterModal: React.FC<MasterModalProps> = ({
  isMasterModalOpen,
  handleMasterModalOpen,
  handleMasterModalClose,
  track,
  addDolbyEffect,
  masteredMusicUrl,
  handleAddTrackVersion,
  state,
  previewState,
  previewLoading,
  setPreviewLoading,
  previewUrl,
  addNewVersion,
  hasPaymentMethod,
  trackUrl,
  trackVersionId,
  setSelectedPreset,
}) => {
  const [stage, setStage] = useState("Details");
  const [selectedPresetValue, setSelectedPresetValue] = useState("a");
  const [selectedPresetArray, setSelectedPresetArray] = useState<string[]>([
    "a",
  ]);
  const [sliderValue, setSliderValue] = useState(-14);
  const [stageAccess, setStageAccess] = useState(1);
  const [previewStartValue, setPreviewStartValue] = useState<number>(0);
  const [duration, setDuration] = useState<number>(30);
  const { getCustomerPortalLink } = useGetCustomerPortalLink();

  const handleAccessStage = (access: number) => {
    if (access >= stageAccess) setStageAccess(access);
  };
  const handleNext = () => {
    Mixpanel.track(`master_stage_${stage}`);
    if (stage === "Details") {
      setStage("Presets");
    }
    if (stage === "Presets") {
      setPreviewLoading(true);
      addDolbyEffect(
        selectedPresetArray,
        sliderValue,
        trackUrl,
        trackVersionId,
        previewStartValue,
        duration
      );
      setStage("Preview");
    }
    if (stage === "Preview") {
      setPreviewLoading(true);
      handleAddTrackVersion(
        sliderValue,
        selectedPresetValue,
        trackUrl,
        trackVersionId
      );
      setStage("Master");
    }
    if (stage === "Master") {
      setStageAccess(1);
      addNewVersion();
    }
  };
  const handleBackButton = () => {
    if (stage === "Preview") {
      setStage("Presets");
    } else if (stage === "Presets") {
      setStage("Details");
    }
  };

  return hasPaymentMethod ? (
    <>
      <div>
        <Button onClick={handleMasterModalOpen}>Open modal</Button>
        <Modal
          open={isMasterModalOpen}
          onClose={handleMasterModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <ModalContainer>
            <Box sx={styles.headerButtonContainer}>
              {navButtons.map((el, i) => {
                return (
                  <NavButtons
                    key={el}
                    sx={
                      stage === el
                        ? { color: APP_COLOR }
                        : { color: TEXT_COLOR }
                    }
                    disabled={i >= stageAccess || previewLoading}
                    onClick={() => setStage(el)}
                  >
                    {el}
                  </NavButtons>
                );
              })}
            </Box>
            <hr />
            {stage === "Details" && (
              <Details
                track={track}
                setPreviewStartValue={setPreviewStartValue}
                setDuration={setDuration}
              />
            )}
            {stage === "Presets" && (
              <ChoosePreset
                setSelectedPresetArray={setSelectedPresetArray}
                selectedPresetArray={selectedPresetArray}
                selectedPresetValue={selectedPresetValue}
                sliderValue={sliderValue}
                setSliderValue={setSliderValue}
                setLoading={setPreviewLoading}
                handleAccessStage={handleAccessStage}
              />
            )}
            {stage === "Preview" && (
              <Preview
                track={track}
                previewUrl={previewUrl}
                masteredMusicUrl={undefined}
                selectedPresetArray={selectedPresetArray}
                setSelectedPresetValue={setSelectedPresetValue}
                loading={previewLoading}
                previewState={previewState}
                handleAccessStage={handleAccessStage}
                previewStartValue={previewStartValue}
                duration={duration}
                setSelectedPreset={setSelectedPreset}
              />
            )}
            {stage === "Master" && (
              <CreateVersion
                track={track}
                masteredMusicUrl={masteredMusicUrl}
                state={state}
                loading={previewLoading}
                handleAccessStage={handleAccessStage}
              />
            )}
            <BottomButtonsContainer
              sx={{
                justifyContent:
                  stage === "Preview" || stage === "Presets"
                    ? "space-between"
                    : "flex-end",
              }}
            >
              {stage === "Preview" || stage === "Presets" ? (
                <GoBackButtonContainer>
                  <Button
                    onClick={handleBackButton}
                    variant="contained"
                    sx={styles.backButton}
                    disabled={previewLoading}
                  >
                    Back
                  </Button>
                </GoBackButtonContainer>
              ) : (
                <></>
              )}
              {stage === "Preview" && !previewLoading && (
                <DetailsContainer>
                  <Typography variant="subtitle1" align="center">
                    {`Master with ${
                      MasteringPresets.find(
                        (el) => el.value === selectedPresetValue
                      )?.title
                    } preset`}
                  </Typography>
                  <Typography variant="caption" align="center" marginTop="2px">
                    Clicking &quot;Master&quot; will invoice you for mastering
                  </Typography>

                  <Typography variant="caption" color="grey" align="left">
                    ($4.99/track for Studio Users, $7.99/track for Major Users &
                    $29.99 for Minor Users)
                  </Typography>
                </DetailsContainer>
              )}
              <GoBackButtonContainer>
                <Button
                  onClick={handleNext}
                  variant="contained"
                  disabled={
                    (stage === "Preview" || stage === "Master") &&
                    previewLoading
                  }
                  sx={styles.stageButton}
                >
                  {stage === "Details" && "Next"}
                  {stage === "Presets" && "Preview"}
                  {stage === "Preview" && "Master"}
                  {stage === "Master" && "Save"}
                </Button>
              </GoBackButtonContainer>
            </BottomButtonsContainer>
          </ModalContainer>
        </Modal>
      </div>
    </>
  ) : (
    <>
      <Button onClick={handleMasterModalOpen}>Open modal</Button>
      <Modal
        open={isMasterModalOpen}
        onClose={handleMasterModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ModalContainer>
          <Box sx={styles.paymentDetailsContainer}>
            <Typography
              variant="h6"
              align="center"
              marginTop="10px"
              marginBottom="10px"
            >
              Enter a payment method to access our Dolby.io AI mastering
              feature.
            </Typography>
            <Typography variant="subtitle2" align="center" marginBottom="40px">
              Mastering a track is $4.99 per track for Studio users, $7.99 per
              track for Major users & $29.99 for Minor users. Dolby AI mastering
              include unlimited 30 second previews of presets. Seamlessly master
              your music to the audio specifications of your favorite streaming
              services / distributors. OmMuse Studio & Major users will also
              have a significant discount on the upcoming Music NFT minting
              feature.
            </Typography>
            <Box sx={styles.enterPaymentMethodButtonContainer}>
              <Button
                onClick={getCustomerPortalLink}
                variant="contained"
                sx={styles.enterPaymentMethodButton}
              >
                Enter Payment Method
              </Button>
              <Button
                onClick={handleMasterModalClose}
                variant="contained"
                sx={styles.dismissButton}
              >
                Dismiss
              </Button>
            </Box>
          </Box>
        </ModalContainer>
      </Modal>
    </>
  );
};
